import { css } from "@emotion/css"
import dynamic from "next/dynamic"
import { useContext, useEffect, useMemo, useState } from "react"

import Spacer from "@p-ban/common/src/components/layout/Spacer"
import { MdInfoOutline } from "react-icons/md"
import useKeepSession from "../../../api/estimate/useKeepSession"
import CalenderInfo from "../../../components/schedule/CalenderInfo/CalenderInfo"
import COLOR_CODE from "./constant/color"
import LAYOUT from "./constant/layout"
import Z_INDEX from "./constant/zIndex"
import Header from "./layout/Header/Header"
import EstimateForm from "./layout/form/EstimateForm"
import Menu from "./layout/menu/Menu"
import MassProductionSimulation from "./layout/modal/MassProductionSimulation/MassProductionSimulation"
import MultipleCountSimulation from "./layout/modal/MultipleCountSimulation/MultipleCountSimulation"
import { ModalProvider } from "./layout/modal/useModal"
import Result from "./layout/result/Result"
import ResultErrorBoundary from "./layout/result/ResultErrorBoundary"
import useDeleteDrcData from "./service/hooks/useDeleteDrcData"
import useEstimateForm from "./service/hooks/useEstimateForm"
import useInitialize from "./service/hooks/useInitialize"
import LINKS from "./constant/links"
import { AuthModal } from "../../Auth/components/AuthModal"
import isDeliveryZeroAvailable from "../service/deliveryZeroAvailabilityChecker"
import type { EstimateDraft } from "../../signup/types/SignUpEstimate"
import useAuthModal from "../../Auth/hooks/useAuthModal"
import { SaveEstimateContext } from "../save/SaveEstimateContext"
import { getCouponCode } from "../../../logic/coupon/referralLink"
import OneClickEstimateContext from "./OneClickEstimateContext"

const ShareLinkModal = dynamic(() => import("./layout/modal/ShareLinkModal/ShareLinkModal"), { ssr: false })

const PARTS_LIST_ERROR_MESSAGE = "部品リストをアップロードしてください。"

const MIN_WIDTH = 1200
const MAX_WIDTH = 1440
const resultWidth = 332
const padding = 16

const OneClickEstimate = () => {
  const [isHydrated, setIsHydrated] = useState(false)
  useEffect(() => setIsHydrated(true), [])

  const { values, handlers, setErrors, errors, resetForm, setFieldValue, params } = useEstimateForm()
  const { isInitialized, initialize } = useInitialize(values)
  useKeepSession()
  useDeleteDrcData()
  const { authModalProps, open } = useAuthModal()
  const url = null // 1Click見積画面からの遷移時は常にnull固定
  const coupon = getCouponCode() ?? undefined

  const {
    values: savedValues,
    manufactureCourse,
    estimateSuccessResult: data,
    bomSnapshotId,
    deliveryZeroFlg,
  } = useContext(SaveEstimateContext)

  const { partsListMapkey } = useContext(OneClickEstimateContext)
  const hasPartsListError = values.partsProcurementCourse && !partsListMapkey

  const estimateDraft: EstimateDraft | undefined = useMemo(() => {
    if (!data) {
      return undefined
    }

    const { summaryTrialPurchaseParam, summaryTrialPurchaseResult } = data

    const customEstimateFlg =
      summaryTrialPurchaseResult.summaryTrialPurchaseDesignResult?.emptyResult === true ||
      summaryTrialPurchaseResult.summaryTrialPurchaseManufactResults.some((r) => r.emptyResult === true) ||
      summaryTrialPurchaseResult.summaryTrialPurchaseMountResult?.emptyResult === true

    return {
      customEstimateFlg,
      estimateParameter: summaryTrialPurchaseParam,
      estimateResult: summaryTrialPurchaseResult,
      manufactureCourse,
      bomSnapshotId: bomSnapshotId ? Number(bomSnapshotId) : null,
      deliveryZeroFlg: isDeliveryZeroAvailable(savedValues, manufactureCourse) ? deliveryZeroFlg : false,
    }
  }, [data, savedValues, manufactureCourse, bomSnapshotId, deliveryZeroFlg])

  return (
    <ModalProvider>
      <div style={{ visibility: isHydrated ? "visible" : "hidden" }}>
        <Header minWidth={MIN_WIDTH} maxWidth={MAX_WIDTH} onLoginClick={() => open({})} />
        <div className={styles.body}>
          <div className={styles.container}>
            <main className={styles.main}>
              <div
                style={{ width: `calc(100% - ${resultWidth + padding}px)` }}
                className="mb-5 w-[1028px] gap-x-[10px] rounded-[8px] bg-white px-4 py-8"
              >
                <div className="px-4">
                  <h2 className="text-[34px] font-bold leading-[normal] text-[#32383e]" style={{ fontFamily: '"Roboto", "Noto Sans JP", sans-serif' }}>
                    1-Click見積
                  </h2>
                </div>
                <div className="mb-16 flex items-center px-4 py-1.5">
                  <MdInfoOutline className="mr-3 h-[22px] w-[22px] text-[#636b74]" />
                  <p className="text-sm text-[#636b74]">
                    見積や仕様についてスタッフに相談したい方は
                    <a
                      href={LINKS.DESIGN_DAIKOU}
                      rel="noopener noreferrer"
                      aria-label="基板設計の詳細見積りフォーム（新しいタブで開く）"
                      target="_blank"
                    >
                      <span className="text-[#636b74] underline">こちら</span>
                    </a>
                  </p>
                </div>
                <CalenderInfo sticky />
                <EstimateForm
                  values={values}
                  handlers={handlers}
                  errors={errors}
                  isInitialized={isInitialized}
                  partsListError={isInitialized && hasPartsListError ? PARTS_LIST_ERROR_MESSAGE : undefined}
                />
              </div>
              <Spacer size={padding} />
              <div className={styles.result}>
                <ResultErrorBoundary values={values}>
                  <Result
                    values={values}
                    params={params}
                    setErrors={setErrors}
                    openLoginModal={open}
                    enabled={isInitialized}
                    startEstimate={initialize}
                    partsListError={hasPartsListError ? PARTS_LIST_ERROR_MESSAGE : undefined}
                  />
                </ResultErrorBoundary>
              </div>
            </main>
            <Menu resetForm={resetForm} />
          </div>
        </div>
      </div>
      <ShareLinkModal params={params} />
      <MultipleCountSimulation params={params} setFieldValue={setFieldValue} />
      <MassProductionSimulation params={params} setFieldValue={setFieldValue} />
      <AuthModal {...authModalProps} estimateDraft={estimateDraft} url={url} coupon={coupon} />
    </ModalProvider>
  )
}

export default OneClickEstimate

const styles = {
  body: css`
    padding-top: ${LAYOUT.HEADER_HEIGHT}px;
    min-width: ${MIN_WIDTH}px;
    min-height: 1080px;
    background-color: ${COLOR_CODE.BACKGROUND_GRAY};
  `,
  container: css`
    max-width: ${MAX_WIDTH}px;
    margin: 0 auto;
  `,
  main: css`
    display: flex;
    padding: 16px 50px 16px 32px;
  `,
  result: css`
    z-index: ${Z_INDEX.RESULT};
    width: ${resultWidth}px;
    position: sticky;
    top: ${LAYOUT.HEADER_HEIGHT + padding}px;
    align-self: flex-start;
  `,
}
